import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { DynamicFormDelegate } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import { requestFormById } from 'api/operations/formService';
import { completeSubscriberChecklistStep, saveGeneralIMQAnswers } from 'api/operations/electionService';
import { getRootStoreInstance } from 'store/rootStore';
import { SubscriberImqModule } from 'store/modules/subscriber-imq/subscriberImqModule';
import { SubChecklistCard } from 'types/electionService';
import { ChecklistCompletionStatus } from 'types/enrollmentService';
import { Primitive } from 'types/general';

export interface EmploymentDetailsDomainParams {
  groupId: string;
  subscriberId: string;
  entityId: string;
}

export default abstract class IEmploymentDetailsPage {
  private readonly _rootStore = getRootStoreInstance();
  protected readonly _domainParams: EmploymentDetailsDomainParams;
  public readonly subscriberImqModule: SubscriberImqModule;

  public dynamicFormDelegate = runInAction(
    () =>
      new DynamicFormDelegate({
        currentUserRoles: this._rootStore.userStore.userRoles,
        fetchForm: async (formId: string): Promise<FormObjectFromJSON> => requestFormById(formId),
        onSubmit: this.completeForm.bind(this),
        disableResetAfterSubmit: true,
      }),
  );

  private _isInitialDataLoading = false;

  public get domainParams(): EmploymentDetailsDomainParams {
    return this._domainParams;
  }

  public get isStepCompleted(): boolean {
    return (
      this.subscriberImqModule
        .getTaskChecklistOptions(this._domainParams.entityId)
        .find((option) => option.card === SubChecklistCard.IMQ_GENERAL_INFO)?.completionStatus ===
      ChecklistCompletionStatus.COMPLETED
    );
  }

  public abstract get isInitialLoading(): boolean;

  public abstract returnBack(): void;

  protected abstract onCompleteRedirect(): void;

  protected get isPageContentLoading(): boolean {
    return this._isInitialDataLoading;
  }

  public setIsInitialDataLoading(isInitialDataLoading: boolean): void {
    this._isInitialDataLoading = isInitialDataLoading;
  }

  public async getInitialData(): Promise<void> {
    this.setIsInitialDataLoading(true);

    await Promise.all([this.subscriberImqModule.getSubscriber(), this.subscriberImqModule.getRequiredSubInfo()]);

    await runInAction(async () => {
      const formId = this.subscriberImqModule.requiredSubInfo?.imq.generalInfoFormId;
      const existingAnswers = this.subscriberImqModule.subscriber?.common ?? {};
      if (!formId) {
        throw new Error('No employment details form configured for IMQ');
      }

      await this.dynamicFormDelegate.buildForm(formId, existingAnswers as Record<string, string>);
      this.setIsInitialDataLoading(false);
    });
  }

  public async completeForm(): Promise<void> {
    await saveGeneralIMQAnswers(
      this._domainParams.subscriberId,
      this.dynamicFormDelegate.formAnswers as Record<string, Primitive>,
    );
    await runInAction(async () => {
      await completeSubscriberChecklistStep({
        subId: this._domainParams.subscriberId,
        entityId: this._domainParams.entityId,
        card: SubChecklistCard.IMQ_GENERAL_INFO,
      });
    });

    runInAction(() => {
      this.onCompleteRedirect();
    });
  }

  constructor(domainParams: EmploymentDetailsDomainParams) {
    this._domainParams = domainParams;
    this.subscriberImqModule = new SubscriberImqModule({ subscriberId: domainParams.subscriberId });

    makeObservable<IEmploymentDetailsPage, '_isInitialDataLoading' | 'isPageContentLoading'>(this, {
      _isInitialDataLoading: observable,
      domainParams: computed,
      isStepCompleted: computed,
      isPageContentLoading: computed,
      setIsInitialDataLoading: action.bound,
      getInitialData: action.bound,
      completeForm: action.bound,
    });
  }
}
